import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router';

import { Box, CircularProgress } from '@mui/material';

import CveItemsStore from 'app/store/CveItemsStore';
import usePageMeta from 'hooks/use-page-meta';
import useSearchParams from 'hooks/use-search-params';
import useStores from 'hooks/use-stores';
import Heading from 'main/components/Heading/Heading';
import Breadcrumbs from 'pages/Search/components/Breadcrumbs';
import SortBlock from 'pages/Search/components/CveList/components/SortBlock';
import SortLink from 'pages/Search/components/CveList/components/SortLink';
import Layout from 'pages/Search/components/Layout';
import Paginator from 'pages/Search/components/Paginator';
import SearchItem from 'pages/Search/components/SearchItem';
import { ROUTES } from 'pages/Search/constants';
import { noneIf, normalizeName } from 'pages/Search/utils';
import isNil from 'utils/is-nil';
import Utils from 'utils/Utils';

import useStyles from './styles';

interface Props {
    variant: 'product' | 'vendor' | 'type';
}

const CveList: React.FC<Props> = ({ variant }) => {
    const { cveItemsStore } = useStores();
    const history = useHistory();
    const { classes } = useStyles();

    const { vendorName, productName, typeName, order = 'date', page } = useParams<Record<string, string>>();

    const [searchParams] = useSearchParams();
    const score = searchParams.score ? Number(searchParams.score) : null;
    const year = searchParams.year ? Number(searchParams.year) : null;
    const month = searchParams.month ? Number(searchParams.month) : null;

    const normalizedProductName = normalizeName(productName);
    const normalizedVendorName = normalizeName(vendorName);
    const normalizedTypeName = normalizeName(typeName);

    const setPath = useCallback(
        ({ page }) => {
            if (variant === 'vendor') {
                return history.push({
                    pathname: ROUTES.VENDOR_CVE({ page, vendorName }),
                    search: history.location.search
                });
            }
            if (variant === 'product') {
                return history.push({
                    pathname: ROUTES.VENDOR_PRODUCT({ page, vendorName, productName }),
                    search: history.location.search
                });
            }
            if (variant === 'type') {
                return history.push({
                    pathname: ROUTES.TYPE({ page, typeName, order: noneIf(order, 'date') }),
                    search: history.location.search
                });
            }
        },
        [variant, history, vendorName, productName, typeName, order]
    );

    const hasMetaPagination = Number(page || 1) > 1;
    const isMostViewed = order === 'most-viewed';

    usePageMeta(
        {
            vendor: {
                title: hasMetaPagination
                    ? `${normalizedVendorName} Vulnerabilities and Security Issues — Page ${page} of up to date CVE List | Vulners.com`
                    : `${normalizedVendorName} Vulnerabilities and Security Issues — up to date CVE List | Vulners.com`,
                description: hasMetaPagination
                    ? `${normalizedVendorName} - Page ${page}. ${normalizedVendorName} security vulnerability list - all security problems and risks of ${normalizedVendorName} vendor in one place with links to detailed description with list of affected software`
                    : `${normalizedVendorName} security vulnerability list - all security problems and risks of ${normalizedVendorName} vendor in one place with links to detailed description with list of affected software`
            },
            product: {
                title: hasMetaPagination
                    ? `${normalizedProductName} Security Vulnerabilities and Issues — Page ${page} of ${normalizedProductName} CVE List | Vulners.com`
                    : `${normalizedProductName} Security Vulnerabilities and Issues — ${normalizedProductName} CVE List | Vulners.com`,
                description: hasMetaPagination
                    ? `${normalizedProductName} - Page ${page}. ${normalizedProductName} vulnerabilities and security issues - all security problems and risks of ${normalizedProductName} in one place with links to detailed description`
                    : `${normalizedProductName} vulnerabilities and security issues - all security problems and risks of ${normalizedProductName} in one place with links to detailed description`
            },
            type: {
                title: hasMetaPagination
                    ? isMostViewed
                        ? `${normalizedTypeName} - Page ${page} of ${normalizedTypeName} Most Viewed Vulnerabilities List | Vulners.com`
                        : `${normalizedTypeName} - Page ${page} of ${normalizedTypeName} Vulnerabilities List | Vulners.com`
                    : isMostViewed
                    ? `${normalizedTypeName} - Most Viewed Vulnerabilities List | Vulners.com`
                    : `${normalizedTypeName} - Vulnerabilities List | Vulners.com`,
                description: hasMetaPagination
                    ? isMostViewed
                        ? `Most Viewed Security Vulnerabilities published by ${normalizedTypeName} - Page ${page}`
                        : `Security Vulnerabilities published by ${normalizedTypeName} - Page ${page}`
                    : isMostViewed
                    ? `Most Viewed Security Vulnerabilities published by ${normalizedTypeName}`
                    : `Security Vulnerabilities published by ${normalizedTypeName}`
            }
        }[variant]
    );

    const layoutRef = useRef<{ scrollTop: () => void }>(null);

    const getPaginationHref = useCallback(
        (page) => {
            if (variant === 'vendor') {
                return ROUTES.VENDOR_CVE({ page: noneIf(page, 1), vendorName }).concat(history.location.search);
            }
            if (variant === 'product') {
                return ROUTES.VENDOR_PRODUCT({ page: noneIf(page, 1), vendorName, productName }).concat(
                    history.location.search
                );
            }
            if (variant === 'type') {
                return ROUTES.TYPE({ page: noneIf(page, 1), typeName, order: noneIf(order, 'date') }).concat(
                    history.location.search
                );
            }
            return '#';
        },
        [history, productName, variant, vendorName, typeName, order]
    );

    const onPageChange = useCallback((page) => setPath({ page }), [setPath]);

    useEffect(() => {
        layoutRef.current?.scrollTop();
        const store = cveItemsStore.setParams({
            vendorName,
            productName,
            typeName,
            page: Number(page || '1'),
            order,
            score,
            year,
            month
        });
        switch (variant) {
            case 'product':
                store.loadProductCveItems();
                break;
            case 'vendor':
                store.loadVendorCveItems();
                break;
            case 'type':
                store.loadTypeCveItems();
                break;
            default:
                break;
        }
    }, [cveItemsStore, page, productName, variant, vendorName, typeName, order, score, year, month]);

    const hasPagination = cveItemsStore.totalCount > cveItemsStore.list.length;

    const breadcrumbs = useMemo(() => {
        if (variant === 'vendor') {
            return [
                { label: 'Vulners', link: ROUTES.ROOT() },
                { label: 'Vendors', link: ROUTES.VENDORS({ page: null, letter: null }) },
                { label: normalizedVendorName, link: ROUTES.VENDOR({ vendorName, page: null }) },
                { label: 'Security Vulnerabilities', link: '', disabled: true }
            ];
        }
        if (variant === 'product') {
            return [
                { label: 'Vulners', link: ROUTES.ROOT() },
                { label: 'Products', link: ROUTES.PRODUCTS({ page: null, letter: null }) },
                { label: `${normalizedProductName} Security Vulnerabilities`, link: '', disabled: true }
            ];
        }
        if (variant === 'type') {
            return [
                { label: 'Vulners', link: ROUTES.ROOT() },
                { label: `${normalizedTypeName} Security Vulnerabilities`, link: '', disabled: true }
            ];
        }
        return [];
    }, [normalizedProductName, normalizedTypeName, normalizedVendorName, variant, vendorName]);

    const title = useMemo(() => {
        const addition = [
            isNil(score) ? '' : `CVSS Score ${score} - ${score + 1}`,
            [month ? Utils.monthOfYear(month - 1) : '', year].filter(Boolean).join(' ')
        ]
            .filter(Boolean)
            .join(' | ');

        if (variant === 'vendor') {
            return [`${normalizedVendorName} Security Vulnerabilities`, addition].filter(Boolean).join(' - ');
        }
        if (variant === 'product') {
            return [`${normalizedProductName} Security Vulnerabilities`, addition].filter(Boolean).join(' - ');
        }
        if (variant === 'type') {
            return isMostViewed
                ? `${normalizedTypeName} - Most Viewed Vulnerabilities List`
                : `${normalizedTypeName} - Vulnerabilities List`;
        }
        return '';
    }, [score, month, year, variant, normalizedVendorName, normalizedProductName, isMostViewed, normalizedTypeName]);

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <>
                    <Heading text={title} orientation="vertical">
                        {variant === 'type' ? (
                            <SortBlock active={order}>
                                <SortLink
                                    label="Date"
                                    value="date"
                                    to={ROUTES.TYPE({ typeName, page: null, order: null })}
                                />
                                <SortLink
                                    label="Views count"
                                    value="most-viewed"
                                    to={ROUTES.TYPE({ typeName, page: null, order: 'most-viewed' })}
                                />
                            </SortBlock>
                        ) : null}
                    </Heading>
                    <Breadcrumbs items={breadcrumbs} className={classes.breadcrumbs} />
                    {cveItemsStore.isLoading ? (
                        <Box className={classes.spinner}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {cveItemsStore.list.map((props, index) => (
                                <SearchItem key={index} {...props} />
                            ))}
                            {hasPagination && (
                                <Paginator
                                    totalCountLabel={'Total number of security vulnerabilities'}
                                    page={cveItemsStore.page}
                                    rowsPerPage={CveItemsStore.PAGE_SIZE}
                                    rowsPerPageOptions={null}
                                    count={Math.min(
                                        CveItemsStore.ORDER_2_LIMIT[order] ?? Infinity,
                                        cveItemsStore.totalCount
                                    )}
                                    onPageChange={onPageChange}
                                    getPaginationHref={getPaginationHref}
                                />
                            )}
                        </>
                    )}
                </>
            }
        />
    );
};

export default observer(CveList);
